import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import API from './API';
import StyledModal from '../Components/GenericModal/StyledModal';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';
import MultiSelectWithCreate from '../Components/CustomUIElements/MultiSelectWithCreate';
import ModalFooterButtons from '../Components/GenericModal/ModalFooterButtons';

const AddToCandidatePool = ({
  isOpen,
  handleClose,
  selectedCandidateGroupIds,
  refetch
}: {
  isOpen: boolean;
  handleClose: () => void;
  selectedCandidateGroupIds: number[];
  refetch?: () => void;
}) => {
  const [selectedPools, setSelectedPools] = useState([]);

  const { data: pools, isLoading: isLoadingPools } = useQuery({
    queryKey: ['candidatePoolsList'],
    queryFn: async () => {
      const res = await API.getCandidatePools();
      return res.res.pools;
    },
    enabled: isOpen
  });

  const { mutate: createCandidatePool, isLoading: creatingPool } = useMutation({
    mutationFn: async (data) => {
      const res = await API.createCandidatePool(data);
      setSelectedPools([...selectedPools, res.res]);
    }
  });

  const { mutate: addCandidatesToPools, isLoading: addingCandidates } = useMutation({
    mutationFn: async () => {
      await API.addCandidatesToPools(selectedCandidateGroupIds, selectedPools.map((pool) => pool.id));
      handleClose();
      refetch && refetch();
    }
  });

  const handlePoolsChange = (value: any) => {
    if (typeof value.at(-1) === 'string') {
      createCandidatePool(value.at(-1));
    } else {
      setSelectedPools(value);
    }
  };

  return (
    <StyledModal
      isOpen={isOpen}
      handleClose={handleClose}
      styleOverrides={{
        maxWidth: '830px',
        maxHeight: '540px'
      }}
      label="add-to-candidate-pool"
      modalTitle={'Add candidates to pools'}
      subHeaderStyling={false}
    >
      <Box sx={{ ...sharedClasses.mainModalContent, height: '100%' }}>
        <Box
          sx={{
            fontFamily: 'Source Sans Pro',
            fontSize: '16px',
            color: '#939393',
            width: '100%',
            paddingInline: '2px'
          }}
        >
          Select or create candidate pools
          <MultiSelectWithCreate
            dropdownOptions={pools || []}
            onChange={(newValues) => handlePoolsChange(newValues)}
            values={selectedPools}
            loadingOptions={isLoadingPools}
            creatingOption={creatingPool}
          />
        </Box>
      </Box>
      <ModalFooterButtons
        primaryButtonText="Add to pools"
        primaryButtonCallback={addCandidatesToPools}
        primaryButtonDisabled={selectedPools.length === 0 || addingCandidates}
        secondaryButtonText="Cancel"
        secondaryButtonCallback={handleClose}
        isLoading={addingCandidates}
        primaryButtonID="add-candidates-button"
        secondaryButtonID="cancel-add-to-pool"
      />
    </StyledModal>
  );
};

export default AddToCandidatePool;
